import type { Ssm5DiagramModule, Ssm5DtcModule, Ssm5ModuleStatus } from '@packages/domain/ssm5';
import type { BadgeVariants, IconProps, StsIconName } from '@packages/ui/shared';

import { colors } from '@packages/core/styles';

/** The look of a diagram cell that is not selected. */
export type CellLook = {
    /** The fill of its box. */
    fill: string;
    /** The color of its border. */
    stroke: string;
};

/** What the technician opened: a cell of the diagram by its id, or a module of the Status and DTCs list by its system. */
export type ModuleSelection =
    | {
          /** The id of the selected cell. */
          id: string;
      }
    | {
          /** The system of the module opened from its card, as the DTC list names it. */
          system: string;
      };

/** The solid look of a selected diagram cell. */
export type SelectedCellLook = {
    /** The fill of its box, which has no border. */
    fill: string;
    /** Whether it casts the design's shadow. */
    hasShadow: boolean;
    /** The color of its text. */
    text: string;
};

/** The chip of a status: the icon in its color on the badge variant, labelled by the legend's key. */
export type StatusChip = {
    /** The icon in the chip. */
    icon: StsIconName;
    /** The color of the icon. */
    iconColor: NonNullable<IconProps['color']>;
    /** The translation key of the label. */
    labelKey: string;
    /** The badge variant that fills the chip. */
    variant: BadgeVariants;
};

/** How a status is drawn: its chip, its cell on the diagram, and the cell once selected where it can be. */
export type StatusLook = {
    /** The cell of the status. */
    cell: CellLook;
    /** The chip of the status, as the legend and the module header draw it. */
    chip: StatusChip;
    /** The cell of the status once selected; absent for a status whose cells cannot be selected. */
    selected?: SelectedCellLook;
};

/** The legend label of each status, as a translation key. */
export const STATUS_LABEL_KEY: Readonly<Record<Ssm5ModuleStatus, string>> = {
    connected: 'ssm5:diagram.legend.connected',
    noConnection: 'ssm5:diagram.legend.noConnection',
    notEquipped: 'ssm5:diagram.legend.notEquipped',
    withErrors: 'ssm5:diagram.legend.withErrors',
};

/** The look of each status, as the design draws its chip and its cell. */
export const STATUS_LOOKS: Readonly<Record<Ssm5ModuleStatus, StatusLook>> = {
    connected: {
        cell: { fill: colors.greenFive, stroke: colors.successLight },
        chip: {
            icon: 'check-circle-hollow',
            iconColor: 'greenOne',
            labelKey: STATUS_LABEL_KEY.connected,
            variant: 'greenFive',
        },
        selected: { fill: colors.successLight, hasShadow: false, text: colors.white },
    },
    noConnection: {
        cell: { fill: colors.redFive, stroke: colors.errorLight },
        chip: {
            icon: 'x-circle',
            iconColor: 'errorLight',
            labelKey: STATUS_LABEL_KEY.noConnection,
            variant: 'redFive',
        },
        selected: { fill: colors.errorLight, hasShadow: false, text: colors.white },
    },
    notEquipped: {
        cell: { fill: colors.grayTwo, stroke: colors.grayFive },
        chip: {
            icon: 'circle-indeterminate',
            iconColor: 'grayFive',
            labelKey: STATUS_LABEL_KEY.notEquipped,
            variant: 'grayTwo',
        },
    },
    withErrors: {
        cell: { fill: colors.yellowFive, stroke: colors.warningMain },
        chip: {
            icon: 'error',
            iconColor: 'warningMain',
            labelKey: STATUS_LABEL_KEY.withErrors,
            variant: 'yellowFive',
        },
        selected: { fill: colors.warningLight, hasShadow: true, text: colors.textDarkPrimary },
    },
};

/** The statuses in the order the legend lists them. */
export const LEGEND_STATUSES: readonly Ssm5ModuleStatus[] = ['connected', 'withErrors', 'noConnection', 'notEquipped'];

/** The status of a cell the backend did not report on. */
export const UNREPORTED_STATUS: Ssm5ModuleStatus = 'notEquipped';

/**
 * A module of the Status and DTCs list as the module panel shows it, for a system the diagram has no
 * module for: named by its system, with errors since it reports DTCs, a primary one when any DTC is not
 * secondary, nothing else known about it.
 *
 * @param dtcModule - the module as the DTC list names it
 * @returns the module for the panel
 */
export function diagramModuleOfDtcModule(dtcModule: Ssm5DtcModule): Ssm5DiagramModule {
    return {
        dtcCount: dtcModule.dtcs.length,
        dtcModules: [dtcModule],
        hasPrimaryDtc: dtcModule.dtcs.some((dtc) => !dtc.isSecondary),
        id: dtcModule.system,
        isReproAvailable: false,
        name: dtcModule.system,
        status: 'withErrors',
    };
}

/**
 * The diagram module a system of the DTC list belongs to: the one whose DTCs are named under that system.
 *
 * @param modules - the modules the backend reported
 * @param system - the system, as the DTC list names it, e.g. "VDC EPB"
 * @returns the module, or undefined when no module reports a DTC of that system
 */
export function diagramModuleOfSystem(
    modules: readonly Ssm5DiagramModule[],
    system: string
): Ssm5DiagramModule | undefined {
    return modules.find((module) => module.dtcModules.some((dtcModule) => dtcModule.system === system));
}

/**
 * Whether a cell with this status can be selected: every cell with data behind it can, a cell that is
 * not equipped cannot.
 *
 * @param status - the cell's status
 * @returns whether the cell can be selected
 */
export function isSelectableStatus(status: Ssm5ModuleStatus): boolean {
    return status !== 'notEquipped';
}

/**
 * The module a selection opens: a cell by its id; a system through the diagram module that reports it,
 * else through its entry in the Status and DTCs list, so a module the diagram does not know still opens.
 *
 * @param selection - what the technician opened, if anything
 * @param modules - the modules the backend reported on the diagram
 * @param listModules - the modules of the Status and DTCs list
 * @returns the module to show, or undefined when nothing is open or nothing matches
 */
export function selectedDiagramModule(
    selection: ModuleSelection | undefined,
    modules: readonly Ssm5DiagramModule[],
    listModules: readonly Ssm5DtcModule[]
): Ssm5DiagramModule | undefined {
    if (selection === undefined) {
        return undefined;
    }

    if ('id' in selection) {
        return modules.find((module) => module.id === selection.id);
    }

    const drawn = diagramModuleOfSystem(modules, selection.system);

    if (drawn !== undefined) {
        return drawn;
    }

    const listed = listModules.find((module) => module.system === selection.system);

    return listed === undefined ? undefined : diagramModuleOfDtcModule(listed);
}

/**
 * The status of each reported module, by the id of its cell.
 *
 * @param modules - the modules the backend reported
 * @returns the status by cell id
 */
export function statusByModuleId(modules: readonly Ssm5DiagramModule[]): ReadonlyMap<string, Ssm5ModuleStatus> {
    return new Map(modules.map((module) => [module.id, module.status]));
}
